/**
 * 管理機能 API（年次更新・お知らせ・マニュアル・操作ログ）。担当: G1
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { masterKeys } from '@/features/masters/api';

// ---------------------------------------------------------------- 年次更新
export interface AnnualUpdateResult {
  operatorSettings: number;
  charterFees: number;
  wages: number;
  zones: number;
  limits: number;
  limitExtras: number;
  ports: number;
  vessels: number;
  captains: number;
  crew: number;
  capacities: number;
  fishingMethods: number;
}

export interface AnnualUpdateRun {
  id: number;
  sourceYear: number;
  targetYear: number;
  result: Partial<AnnualUpdateResult>;
  executedAt: string;
  executedBy: number | null;
  executedByName: string | null;
}

export const adminKeys = {
  all: ['admin'] as const,
  runs: () => [...adminKeys.all, 'annual-update-runs'] as const,
  announcements: () => [...adminKeys.all, 'announcements'] as const,
  active: () => ['announcements', 'active'] as const,
  manuals: () => [...adminKeys.all, 'manuals'] as const,
  audit: (f: unknown) => [...adminKeys.all, 'audit-logs', f] as const,
};

export function useAnnualUpdateRuns() {
  return useQuery({ queryKey: adminKeys.runs(), queryFn: ({ signal }) => api.get<AnnualUpdateRun[]>('/admin/annual-update/runs', undefined, { signal }) });
}

export function useRunAnnualUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { sourceYear: number; targetYear: number }) => api.post<{ result: AnnualUpdateResult }>('/admin/annual-update', { ...body, confirm: true }),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: adminKeys.runs() }), qc.invalidateQueries({ queryKey: masterKeys.all }), qc.invalidateQueries({ queryKey: ['fiscal-years'] }), qc.invalidateQueries({ queryKey: ['vessels'] })]),
  });
}

// ---------------------------------------------------------------- お知らせ
export interface AnnouncementDto {
  id: number;
  title: string;
  body: string | null;
  url: string | null;
  isVisible: boolean;
  displayOrder: number;
  publishFrom: string | null;
  publishTo: string | null;
}

export interface ActiveAnnouncement {
  id: number;
  title: string;
  body: string | null;
  url: string | null;
  publishFrom: string | null;
}

/** 掲載中のお知らせ（全ロール。ポータルから利用する） */
export function useActiveAnnouncements() {
  return useQuery({
    queryKey: adminKeys.active(),
    queryFn: ({ signal }) => api.get<ActiveAnnouncement[]>('/announcements/active', undefined, { signal }),
    staleTime: 60_000,
  });
}

export function useAnnouncements() {
  return useQuery({ queryKey: adminKeys.announcements(), queryFn: ({ signal }) => api.get<AnnouncementDto[]>('/admin/announcements', undefined, { signal }) });
}

export function useSaveAnnouncement(id: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Omit<AnnouncementDto, 'id'>) => (id == null ? api.post<AnnouncementDto>('/admin/announcements', body) : api.put<AnnouncementDto>(`/admin/announcements/${id}`, body)),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: adminKeys.announcements() }), qc.invalidateQueries({ queryKey: adminKeys.active() })]),
  });
}

export function useDeleteAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/admin/announcements/${id}`),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: adminKeys.announcements() }), qc.invalidateQueries({ queryKey: adminKeys.active() })]),
  });
}

// ---------------------------------------------------------------- マニュアル
export interface ManualDto {
  id: number;
  version: number;
  title: string;
  note: string | null;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
  isLatest: boolean;
}

export function useManuals() {
  return useQuery({ queryKey: adminKeys.manuals(), queryFn: ({ signal }) => api.get<ManualDto[]>('/admin/manuals', undefined, { signal }) });
}

export function useUploadManual() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: FormData) => api.post<{ id: number; version: number; title: string }>('/admin/manuals', form),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.manuals() }),
  });
}

// ---------------------------------------------------------------- 操作ログ
export interface AuditLogDto {
  id: number;
  createdAt: string;
  userId: number | null;
  loginId: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  fiscalYear: number | null;
  detail: unknown;
  ipAddress: string | null;
}

export interface AuditFilter {
  from?: string;
  to?: string;
  loginId?: string;
  action?: string;
  targetType?: string;
}

export function useAuditLogs(filter: AuditFilter, page: number, pageSize: number) {
  return useQuery({
    queryKey: adminKeys.audit({ filter, page, pageSize }),
    queryFn: ({ signal }) => api.get<{ items: AuditLogDto[]; total: number }>('/admin/audit-logs', { ...filter, page, pageSize }, { signal }),
    placeholderData: keepPreviousData,
  });
}
